import assert from "assert";
import { clamp01 } from "common/utils/math";
import * as THREE from "three";
import { MathUtils } from "three";
import type {
  DesignMaterialParamsPreprocessed,
  LayerParamsPreprocessed,
  SdfParamsPreprocessed,
} from "./designMaterialTypes";
import { blendColors, type RGBArray } from "./shaders/blending";
import { textureAverageValues, textureNames } from "./textureNames";

function textureAverageValue(textureName: string): number {
  const index = textureNames.indexOf(textureName as any);
  assert(index !== -1, `Texture name ${textureName} not found in textureNames`);
  return textureAverageValues[index];
}

function layerAverageValue(layer: LayerParamsPreprocessed): { average: number; variance: number } {
  let average = 1.0;
  let variance = 0.0;
  if (layer.srcID === "texture") {
    average = textureAverageValue(layer.textureName);
    variance = 0.5;
  }
  if (layer.srcID === "SDFDepthMask") {
    average = 0.5;
    variance = 0.5;
  }
  if (layer.srcID === "SDFRandom") {
    average = 0.5;
    variance = 0.5;
  }
  if (layer.invertInput) {
    average = 1.0 - average;
  }
  return { average, variance };
}

function layerSdfCoverage(layer: LayerParamsPreprocessed, sdf: SdfParamsPreprocessed): number {
  let patternCount = sdf.patterns.length;
  if (sdf.common.randomTilesMode === "RandomSplit") {
    patternCount = Math.min(patternCount, 1);
  }
  // This is a very coarse approximation of how much of the 2D area is covered by the SDF patterns.
  // We just sum the patterns together, not taking overlap into account.
  // It's good enough for the flat color approximation.
  let coverage = 0.0;
  for (let patternIndex = 0; patternIndex < patternCount; patternIndex++) {
    const pattern = sdf.patterns[patternIndex];
    if (layer.sdfMaskPatterns & (1 << patternIndex)) {
      coverage += (pattern.xSize / pattern.xDistance) * (pattern.ySize / pattern.yDistance);
    }
  }
  return Math.min(coverage, 1.0);
}

function layerEvaluateGradient(layer: LayerParamsPreprocessed, t: number): THREE.Color {
  if (layer.colorCount === 4 && t > layer.color2Pos) {
    const tApplied = clamp01((t - layer.color2Pos) / Math.max(layer.color3Pos - layer.color2Pos, 0.0001));
    return new THREE.Color(layer.color2).lerp(new THREE.Color(layer.color3), tApplied);
  } else if (layer.colorCount > 2 && t > layer.color1Pos) {
    const tApplied = clamp01((t - layer.color1Pos) / Math.max(layer.color2Pos - layer.color1Pos, 0.0001));
    return new THREE.Color(layer.color1).lerp(new THREE.Color(layer.color2), tApplied);
  } else {
    const tApplied = clamp01((t - layer.color0Pos) / Math.max(layer.color1Pos - layer.color0Pos, 0.0001));
    return new THREE.Color(layer.color0).lerp(new THREE.Color(layer.color1), tApplied);
  }
}

/**
 * Generate a flat color approximation of a design material.
 * @param params The design material parameters.
 * @returns RGB array in linear color space.
 */
export function designMaterialAsFlatColor(params: DesignMaterialParamsPreprocessed): RGBArray {
  // This converts the color from a hex string to linear color space, where we perform blending.
  let color: RGBArray = new THREE.Color(params.color).toArray() as RGBArray;

  for (const layer of params.layers) {
    let maskAverage = 1;
    if (layer.maskID === "SDFMask") {
      maskAverage = layer.sdfMaskRandomAverage * layerSdfCoverage(layer, params.sdf);
    } else if (layer.maskID.startsWith("Mask")) {
      const maskIndex = parseInt(layer.maskID.substring(4));
      const mask = params.masks[maskIndex];
      maskAverage =
        MathUtils.smoothstep(textureAverageValue(mask.textureName), mask.contrast, 1.0 - mask.contrast) * mask.strength;
    }
    if (layer.invertMask) {
      maskAverage = 1.0 - maskAverage;
    }
    const blendStrength = layer.strength * layer.colorInfluence * maskAverage;

    // Three samples with the three gradient colors.

    const { average, variance } = layerAverageValue(layer);

    // The gradient might not be linear, and blending operations are not linear either,
    // so we get better results by evaluating the gradient at multiple points,
    // blending all of the alternative colors with the base color, and then averaging the results.
    // Check Brick_Che_Augusta layer config for an example of where this makes a difference.
    const layerColor0: RGBArray = layerEvaluateGradient(layer, average).toArray() as RGBArray;
    const layerColor1: RGBArray = layerEvaluateGradient(layer, average + variance).toArray() as RGBArray;
    const layerColor2: RGBArray = layerEvaluateGradient(layer, average - variance).toArray() as RGBArray;

    const color0 = blendColors(color, layerColor0, blendStrength, layer.blendMode);
    const color1 = blendColors(color, layerColor1, blendStrength, layer.blendMode);
    const color2 = blendColors(color, layerColor2, blendStrength, layer.blendMode);
    color = [
      (color0[0] + color1[0] + color2[0]) / 3,
      (color0[1] + color1[1] + color2[1]) / 3,
      (color0[2] + color1[2] + color2[2]) / 3,
    ];
  }
  color[0] = clamp01(color[0]);
  color[1] = clamp01(color[1]);
  color[2] = clamp01(color[2]);
  return color;
}

/**
 * Generate a flat color approximation of a design material, in sRGB color space.
 * @param params The design material parameters.
 * @returns CSS hex string in sRGB color space.
 */
export function designMaterialAsFlatColorSRGB(params: DesignMaterialParamsPreprocessed): string {
  const color = designMaterialAsFlatColor(params);
  return "#" + new THREE.Color(color[0], color[1], color[2]).getHexString();
}
